import { api, queryClient } from '@/lib/api-client.ts'
import { AxiosResponse, AxiosError } from 'axios'
import {
  SingleStatus,
  SingleStatusGroup,
  StatusGroups,
} from '@/routes/settings/statuses/types/status.ts'
import { convertFormData, getCurrentOrganization } from '@/lib/utils.ts'
import { useAllStatusGroupStore } from '@/routes/settings/statuses/state/allGroups/allStatusGroupStore.ts'

type QuickEditData = {
  name: string | undefined
  icon: string | undefined | null
  isFinal: boolean
}

export type CreateStatusData = {
  name: string
  icon: string
  statusGroup: string
  priority: number
  isFinal: boolean
}

type EditStatusGroupData = {
  name?: string
  description?: string
  statuses?: StatusesOrder[]
}

export type StatusesOrder = { statusId: number | undefined; priority: number | undefined }

export const organizationStatusGroupsQuery = ({
  projectId = undefined,
}: {
  projectId: string | undefined
}) => ({
  queryKey: [projectId ? 'projectStatusGroups' : 'organizationStatusGroups'],
  queryFn: () => (projectId ? getProjectStatusGroups(projectId) : getStatusGroups()),
})

export const getOrganizationStatusGroups = async ({
  projectId = undefined,
}: {
  projectId?: string | undefined
  view?: string
}) => {
  return await queryClient.ensureQueryData(organizationStatusGroupsQuery({ projectId }))
}

export const getProjectStatusGroups = async (projectId: string | undefined) => {
  const { setStatusGroups } = useAllStatusGroupStore.getState()

  if (!projectId) {
    return null
  }
  try {
    const response = await api.get(`/projects/${projectId}/status_groups`)
    const statusGroups = response.data as StatusGroups
    setStatusGroups(statusGroups)
    return statusGroups
  } catch (error) {
    if (error instanceof AxiosError) {
      return null
    }
  }
  return null
}
export const getStatusGroups = async () => {
  const organizationId = getCurrentOrganization()?.organization?.id
  const { setStatusGroups } = useAllStatusGroupStore.getState()

  if (!organizationId) {
    return null
  }
  try {
    const response = await api.get(`/organizations/${organizationId}/status_groups`)
    const statusGroups = response.data as StatusGroups
    setStatusGroups(statusGroups)
    return statusGroups
  } catch (error) {
    if (error instanceof AxiosError) {
      return null
    }
  }
  return null
}

export const editStatus = async (
  status: SingleStatus,
  quickEditData: QuickEditData,
): Promise<AxiosResponse<SingleStatus>> => {
  return api.patch(`/statuses/${status.id}`, {
    name: quickEditData.name,
    icon: quickEditData.icon,
    isFinal: quickEditData.isFinal,
  })
}

export const deleteStatus = async (status: SingleStatus): Promise<AxiosResponse<SingleStatus>> => {
  return api.delete(`/statuses/${status.id}`)
}

export const createStatus = async (
  createStatusData: CreateStatusData,
): Promise<AxiosResponse<SingleStatus>> => {
  return api.post(`/statuses`, createStatusData)
}

export const editStatusGroup = async (
  statusGroup: SingleStatusGroup,
  data: EditStatusGroupData,
): Promise<AxiosResponse<SingleStatusGroup>> => {
  return api.patch(`/status_groups/${statusGroup.id}`, data)
}

export const deleteStatusGroup = async (
  statusGroup: SingleStatusGroup,
): Promise<AxiosResponse<null>> => {
  return api.delete(`/status_groups/${statusGroup.id}`)
}

export const createStatusGroup = async (
  formData: FormData,
): Promise<AxiosResponse<SingleStatusGroup>> => {
  return api.post(`/status_groups`, convertFormData(formData))
}
